import type { AiUsageRecord } from "../../../shared/aiUsageRecords";
import { recordCost } from "./aiUsage";
import { classifyAiUsageActivity } from "./aiUsageActivities";

export const PERSONAL_CHAT_BUCKET = "personal-chat";
export const UNATTRIBUTED_BUCKET = "unattributed";

export type CostActivity = "build" | "run" | "monitor" | "other";

export type AiUsageTotals = {
  tokens: number;
  requestCount: number;
  cost: number | null;
  activityCost: Record<CostActivity, number | null>;
};

// Personal spaces stay separate so an admin can see whose usage it is.
export function aiUsageBucketId(record: {
  spaceId: string | null;
  personal: boolean;
}): string {
  if (record.spaceId) return record.spaceId;
  return record.personal ? PERSONAL_CHAT_BUCKET : UNATTRIBUTED_BUCKET;
}

function costActivity(source: string): CostActivity {
  return classifyAiUsageActivity(source) ?? "other";
}

function addCost(total: number | null, cost: number | null): number | null {
  return cost == null ? total : (total ?? 0) + cost;
}

export function groupAiUsage(
  records: readonly AiUsageRecord[],
  prices: ReadonlyMap<string, number>,
  keyOf: (record: AiUsageRecord) => string | null
): Map<string, AiUsageTotals> {
  const groups = new Map<string, AiUsageTotals>();
  for (const record of records) {
    const key = keyOf(record);
    if (key == null) continue;
    const totals = groups.get(key) ?? {
      tokens: 0,
      requestCount: 0,
      cost: null,
      activityCost: { build: null, run: null, monitor: null, other: null },
    };
    const cost = recordCost(record, prices);
    const activity = costActivity(record.source);
    totals.tokens += record.inputTokens + record.outputTokens;
    totals.requestCount += record.requestCount;
    totals.cost = addCost(totals.cost, cost);
    totals.activityCost[activity] = addCost(
      totals.activityCost[activity],
      cost
    );
    groups.set(key, totals);
  }
  for (const [key, totals] of groups) {
    if (totals.tokens === 0 && totals.requestCount === 0) groups.delete(key);
  }
  return groups;
}
